import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import "./PostCard.scss";
import CommentsModal from "../../Comments/CommentsModal";
import ShareModal from "../../Shared/ShareModal";
import SaveSheetModal from "../../Saved/SaveSheetModal";
import { getCurrentUserId, getAccessToken } from "../../../awsAuth";
import PhotoDetails from "../../PhotoDetails/PhotoDetails";
import ActionBar from "../../common/ActionBar";

const DEFAULT_AVATAR =
  "https://cvanto-photos.s3.us-east-1.amazonaws.com/DefaultAvatar/avatar.png";

const API =
  process.env.REACT_APP_API_BASE ||
  process.env.REACT_APP_API ||
  (typeof window !== "undefined" &&
  window.location.hostname.includes("onrender.com")
    ? "https://cvanto-api.onrender.com"
    : "http://localhost:5000");
    
const ENABLE_LISTING_STATS =
  process.env.REACT_APP_ENABLE_LISTING_STATS === "1";

function formatTimeAgo(ts) {
  if (!ts) return "";
  const d = typeof ts === "number" ? new Date(ts) : new Date(ts);
  const now = new Date();
  const diffMs = now - d;
  const diffMin = Math.floor(diffMs / 60000);
  const diffH = Math.floor(diffMin / 60);
  const diffD = Math.floor(diffH / 24);
  if (diffMin < 1) return "щойно";
  if (diffMin < 60) return `${diffMin} хв тому`;
  if (diffH < 24) return `${diffH} год тому`;
  if (diffD === 1) return "вчора";
  const months = [
    "січня","лютого","березня","квітня","травня","червня",
    "липня","серпня","вересня","жовтня","листопада","грудня",
  ];
  return `${d.getDate()} ${months[d.getMonth()]}`;
}

const apiUrl = (path) => {
  if (!path) return API;
  if (/^https?:\/\//i.test(path)) return path;
  const base = String(API || "").replace(/\/+$/, "");
  const p = String(path || "");
  return p.startsWith("/") ? `${base}${p}` : `${base}/${p}`;
};

export default function PostCard({
  item,
  isSaved,
  isLiked,
  likesCount,
  onToggleLike,
  onToggleSave,
  onOpenActions,
  navigate,
  timeAgo,
  onOpenComments,
  ownerView = false,
  onSyncCounts,
  allowComments = true,
  disableInitialTruthFetch = false,
}) {
  const postId = useMemo(
    () => item?.postId || item?.id || item?.listingId || item?._id,
    [item]
  );

  const authorId = item?.userId || item?.authorId || null;

  const [localLiked, setLocalLiked] = useState(!!isLiked);
  const [localSaved, setLocalSaved] = useState(!!isSaved);

  const [likeCount, setLikeCount] = useState(Number(likesCount || 0));
  const [commentCount, setCommentCount] = useState(Number(item?.commentsCount || 0));
  const [shareCount, setShareCount] = useState(Number(item?.sharesCount || 0));
  const [savedCount, setSavedCount] = useState(Number(item?.savesCount || item?.favoritesCount || 0));

  const [saveSheetOpen, setSaveSheetOpen] = useState(false);

  const likedStateRef = useRef(!!isLiked);
  const savedStateRef = useRef(!!isSaved);

  useEffect(() => {
    likedStateRef.current = !!localLiked;
  }, [localLiked]);

  useEffect(() => {
    savedStateRef.current = !!localSaved;
  }, [localSaved]);

  const likeUiRef = useRef(Number(likesCount || 0));
  const commentUiRef = useRef(Number(item?.commentsCount || 0));
  const shareUiRef = useRef(Number(item?.sharesCount || 0));
  const savedUiRef = useRef(Number(item?.favoritesCount || 0));

  useEffect(() => { likeUiRef.current = Number.isFinite(likeCount) ? likeCount : 0; }, [likeCount]);
  useEffect(() => { commentUiRef.current = Number.isFinite(commentCount) ? commentCount : 0; }, [commentCount]);
  useEffect(() => { shareUiRef.current = Number.isFinite(shareCount) ? shareCount : 0; }, [shareCount]);
  useEffect(() => { savedUiRef.current = Number.isFinite(savedCount) ? savedCount : 0; }, [savedCount]);

  const MUT_GUARD_MS = 2500;

  const likeMutationRef = useRef({ ts: 0, expected: null, expectedLiked: null });
  const shareMutationRef = useRef({ ts: 0, expected: null });
  const savedMutationRef = useRef({ ts: 0, expected: null, expectedSaved: null });

  const likesFetchRef = useRef(0);
  const fetchLikesTruth = async () => {
  if (!postId) return;
  if (disableInitialTruthFetch) return;

  const startedAt = Date.now();
  likesFetchRef.current = startedAt;

  try {
    const token = await getAccessToken();

    const headers = token
      ? { Authorization: `Bearer ${token}` }
      : {};

    const resp = await fetch(
      apiUrl(`/api/interactions/like/${encodeURIComponent(postId)}?_ts=${Date.now()}`),
      {
        cache: "no-store",
        headers,
      }
    );

    if (!resp.ok) {
      console.error("[post likes truth failed]", resp.status);
      return;
    }

    const json = await resp.json().catch(() => ({}));

    if (likesFetchRef.current !== startedAt) return;

    const n = Number(json?.count ?? 0);
    const likedByMe = !!json?.viewer?.isLiked;

    const ts = likeMutationRef.current?.ts || 0;
    const expected = likeMutationRef.current?.expected;
    const expectedLiked = likeMutationRef.current?.expectedLiked;

    if (Date.now() - ts < MUT_GUARD_MS) {
      if (Number.isFinite(expected) && Number.isFinite(n) && n !== expected) return;
      if (typeof expectedLiked === "boolean" && likedByMe !== expectedLiked) return;
    }

    if (Number.isFinite(n)) {
      const next = Math.max(0, n);
      setLikeCount(next);
      onSyncCounts?.({ postId, likesCount: next });
    }

    setLocalLiked(!!likedByMe);
    likedStateRef.current = !!likedByMe;
  } catch (err) {
    console.error("[post fetchLikesTruth error]", err);
  }
};

  const commentsFetchRef = useRef(0);
  const fetchCommentCount = async () => {
    if (!postId) return;
    const startedAt = Date.now();
    commentsFetchRef.current = startedAt;
    try {
      const r = await fetch(
        apiUrl(`/api/comments/stats/${encodeURIComponent(postId)}?_ts=${Date.now()}`),
        { cache: "no-store" }
      );
      if (!r.ok) return;
      const j = await r.json().catch(() => ({}));
      if (commentsFetchRef.current !== startedAt) return;
      const n = Number(j?.count || 0);
      if (Number.isFinite(n)) {
        setCommentCount(Math.max(0, n));
        onSyncCounts?.({ postId, commentsCount: Math.max(0, n) });
      }
    } catch {}
  };

  const sharesFetchRef = useRef(0);
  const fetchShareCount = async () => {
    if (!postId) return;
    const startedAt = Date.now();
    sharesFetchRef.current = startedAt;
    try {
      const r = await fetch(
        apiUrl(`/api/shares/count/${encodeURIComponent(postId)}?type=post&_ts=${Date.now()}`),
        { cache: "no-store" }
      );
      if (!r.ok) return;
      const j = await r.json().catch(() => ({}));
      if (sharesFetchRef.current !== startedAt) return;

      const n = Number(j?.count || 0);

      const ts = shareMutationRef.current?.ts || 0;
      const expected = shareMutationRef.current?.expected;

      if (Number.isFinite(expected) && Date.now() - ts < MUT_GUARD_MS && n !== expected) {
        return;
      }

      if (Number.isFinite(n)) {
        setShareCount(Math.max(0, n));
        onSyncCounts?.({ postId, sharesCount: Math.max(0, n) });
      }
    } catch {}
  };

  const savedFetchRef = useRef(0);
  const fetchSavedCount = async () => {
    if (!postId) return;
    const startedAt = Date.now();
    savedFetchRef.current = startedAt;
    try {
      const resp = await fetch(
        apiUrl(`/api/saved/count/${encodeURIComponent(postId)}?type=post&_ts=${Date.now()}`),
        { cache: "no-store" }
      );
      if (!resp.ok) return;
      const json = await resp.json().catch(() => ({}));
      if (savedFetchRef.current !== startedAt) return;

      const n = Number(json?.count);

      const ts = savedMutationRef.current?.ts || 0;
      const expected = savedMutationRef.current?.expected;

      if (Number.isFinite(expected) && Date.now() - ts < MUT_GUARD_MS && n !== expected) {
        return;
      }

      if (Number.isFinite(n)) {
        setSavedCount(Math.max(0, n));
        onSyncCounts?.({ postId, savesCount: Math.max(0, n) });
      }
    } catch {}
  };

  const savedStateFetchRef = useRef(0);
  const fetchSavedState = async () => {
    const me = getCurrentUserId?.();
    if (!postId || !me) return;

    const startedAt = Date.now();
    savedStateFetchRef.current = startedAt;

    try {
      const token = await getAccessToken().catch(() => null);
      const resp = await fetch(
        apiUrl(
          `/api/saved/check?userId=${encodeURIComponent(me)}&postId=${encodeURIComponent(
            postId
          )}&type=post&_ts=${Date.now()}`
        ),
        { cache: "no-store", headers: { Authorization: `Bearer ${token}` } }
      );
      if (!resp.ok) return;
      const json = await resp.json().catch(() => ({}));
      if (savedStateFetchRef.current !== startedAt) return;

      const willBeSaved = !!json?.saved;

      const ts = savedMutationRef.current?.ts || 0;
      const expectedSaved = savedMutationRef.current?.expectedSaved;

      if (Date.now() - ts < MUT_GUARD_MS && typeof expectedSaved === "boolean") {
        if (willBeSaved !== expectedSaved) return;
      }

      setLocalSaved(willBeSaved);
      savedStateRef.current = willBeSaved;
    } catch {}
  };

    useEffect(() => {
  setLikeCount(Number(likesCount || 0));
  setLocalLiked(!!isLiked);
  likedStateRef.current = !!isLiked;

  setLocalSaved(!!isSaved);
  savedStateRef.current = !!isSaved;

  setCommentCount(Number(item?.commentsCount || 0));
  setShareCount(Number(item?.sharesCount || 0));
  setSavedCount(Number(item?.savesCount || item?.favoritesCount || 0));
}, [
  postId,
  likesCount,
  isLiked,
  isSaved,
  item?.commentsCount,
  item?.sharesCount,
  item?.savesCount,
  item?.favoritesCount,
]);

  useEffect(() => {
    if (!postId) return;

    const ts = likeMutationRef.current?.ts || 0;
    if (Date.now() - ts >= MUT_GUARD_MS) {
      setLikeCount(Number(likesCount || 0));
      setLocalLiked(!!isLiked);
      likedStateRef.current = !!isLiked;
    }

    const tsS = savedMutationRef.current?.ts || 0;
    if (Date.now() - tsS >= MUT_GUARD_MS) {
      setLocalSaved(!!isSaved);
      savedStateRef.current = !!isSaved;
    }
  }, [postId, likesCount, isLiked, isSaved]);

  useEffect(() => {
    const onComments = (e) => {
      const detail = e?.detail || {};
      const id = detail.postId || detail.listingId;
      if (!id || id !== postId) return;

      const abs = Number(detail.absolute);
      if (Number.isFinite(abs)) {
        setCommentCount(Math.max(0, abs));
        onSyncCounts?.({ postId, commentsCount: Math.max(0, abs) });
        setTimeout(() => fetchCommentCount(), 140);
        return;
      }

      const delta = Number(detail.delta || 0);
      if (delta) {
        const nextAbs = Math.max(0, (commentUiRef.current || 0) + delta);
        setCommentCount(nextAbs);
        onSyncCounts?.({ postId, commentsCount: nextAbs });
        if (ENABLE_LISTING_STATS) setTimeout(() => fetchCommentCount(), 140);
      } else {
        setTimeout(() => fetchCommentCount(), 140);
      }
    };

    const onShares = (e) => {
      const detail = e?.detail || {};
      const id = detail.postId || detail.listingId;
      if (!id || id !== postId) return;

      const abs = Number(detail.absolute);
      if (Number.isFinite(abs)) {
        const nextAbs = Math.max(0, abs);
        setShareCount(nextAbs);
        shareMutationRef.current = { ts: Date.now(), expected: nextAbs };
        onSyncCounts?.({ postId, sharesCount: nextAbs });
        setTimeout(() => fetchShareCount(), 220);
        return;
      }

      const delta = Number(detail.delta || 0);
      if (delta) {
        const nextAbs = Math.max(0, (shareUiRef.current || 0) + delta);
        setShareCount(nextAbs);
        shareMutationRef.current = { ts: Date.now(), expected: nextAbs };
        onSyncCounts?.({ postId, sharesCount: nextAbs });
        setTimeout(() => fetchShareCount(), 220);
      } else {
        setTimeout(() => fetchShareCount(), 220);
      }
    };

    const onSaved = (e) => {
      const detail = e?.detail || {};
      const id = detail.postId || detail.listingId;
      if (!id || id !== postId) return;

      if (typeof detail.saved === "boolean") {
        setLocalSaved(!!detail.saved);
        savedStateRef.current = !!detail.saved;
      }

      const abs = Number(detail.absolute);
      if (Number.isFinite(abs)) {
        const nextAbs = Math.max(0, abs);
        setSavedCount(nextAbs);
        savedMutationRef.current = {
          ts: Date.now(),
          expected: nextAbs,
          expectedSaved: typeof detail.saved === "boolean" ? !!detail.saved : savedStateRef.current,
        };
        onSyncCounts?.({ postId, savesCount: nextAbs });
        setTimeout(() => {
          fetchSavedCount();
          fetchSavedState();
        }, 220);
        return;
      }

      const delta = Number(detail.delta || 0);
      if (delta) {
        const nextAbs = Math.max(0, (savedUiRef.current || 0) + delta);
        setSavedCount(nextAbs);
        savedMutationRef.current = {
          ts: Date.now(),
          expected: nextAbs,
          expectedSaved: typeof detail.saved === "boolean" ? !!detail.saved : savedStateRef.current,
        };
        onSyncCounts?.({ postId, savesCount: nextAbs });
        setTimeout(() => {
          fetchSavedCount();
          fetchSavedState();
        }, 220);
      } else {
        setTimeout(() => {
          fetchSavedCount();
          fetchSavedState();
        }, 220);
      }
    };

    const onLikes = (e) => {
      const detail = e?.detail || {};
      const id = detail.postId || detail.listingId;
      if (!id || id !== postId) return;

      if (typeof detail.liked === "boolean") {
        setLocalLiked(!!detail.liked);
        likedStateRef.current = !!detail.liked;
      }

      const abs = Number(detail.absolute);
      if (Number.isFinite(abs)) {
        const nextAbs = Math.max(0, abs);
        setLikeCount(nextAbs);
        likeMutationRef.current = {
          ts: Date.now(),
          expected: nextAbs,
          expectedLiked: typeof detail.liked === "boolean" ? !!detail.liked : likedStateRef.current,
        };
        onSyncCounts?.({ postId, likesCount: nextAbs });
        setTimeout(() => {
          if (!disableInitialTruthFetch) fetchLikesTruth();
        }, 220);
        return;
      }

      const delta = Number(detail.delta || 0);
      if (delta) {
        const nextAbs = Math.max(0, (likeUiRef.current || 0) + delta);
        setLikeCount(nextAbs);
        likeMutationRef.current = {
          ts: Date.now(),
          expected: nextAbs,
          expectedLiked: typeof detail.liked === "boolean" ? !!detail.liked : likedStateRef.current,
        };
        onSyncCounts?.({ postId, likesCount: nextAbs });
        setTimeout(() => {
          if (!disableInitialTruthFetch) fetchLikesTruth();
        }, 220);
      } else {
        setTimeout(() => {
          if (!disableInitialTruthFetch) fetchLikesTruth();
        }, 220);
      }
    };

    window.addEventListener("cv-comments-updated", onComments);
    window.addEventListener("cv-shares-updated", onShares);
    window.addEventListener("cv-saved-updated", onSaved);
    window.addEventListener("cv-likes-updated", onLikes);

    return () => {
      window.removeEventListener("cv-comments-updated", onComments);
      window.removeEventListener("cv-shares-updated", onShares);
      window.removeEventListener("cv-saved-updated", onSaved);
      window.removeEventListener("cv-likes-updated", onLikes);
    };
  }, [postId, onSyncCounts]);

  const [commentsOpen, setCommentsOpen] = useState(false);

  const handleOpenComments = () => {
    if (!allowComments) return;

    if (typeof onOpenComments === "function") {
      onOpenComments(item);
      return;
    }
    if (postId) setCommentsOpen(true);
  };

  const [shareOpen, setShareOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [shareText, setShareText] = useState("");

  const openShare = (e) => {
    e?.stopPropagation?.();
    if (!postId) return;

    const url = `${window.location.origin}/post/${postId}`;
    const txt = item?.title || (item?.text ? String(item.text).slice(0, 140) : "");
    setShareUrl(url);
    setShareText(txt || "");
    setShareOpen(true);
  };

  const handleShared = async (recipients = []) => {
    if (!postId) return;

    const add = Math.max(1, Number(recipients?.length || 0));
    const curAbs = Number.isFinite(shareUiRef.current) ? shareUiRef.current : 0;
    const expectedAbs = Math.max(0, curAbs + add);

    // 1) миттєво UI
    setShareCount(expectedAbs);
    onSyncCounts?.({ postId, sharesCount: expectedAbs });

    // 2) guard
    shareMutationRef.current = { ts: Date.now(), expected: expectedAbs };

    // 3) глобальна подія
    try {
      window.dispatchEvent(
        new CustomEvent("cv-shares-updated", {
          detail: { postId, delta: add, absolute: expectedAbs },
        })
      );
    } catch {}

    // 4) бекенд (синхронізація)
    const me = getCurrentUserId?.();
    try {
      for (let i = 0; i < add; i += 1) {
        try {
          const token = await getAccessToken();
if (!token) {
  navigate?.("/auth", { state: { from: `/post/${postId}` } });
  return;
}

await fetch(apiUrl(`/api/shares`), {
  method: "POST",
  headers: {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  },
  body: JSON.stringify({
    postId,
    userId: me || null,
    channel: "direct",
    contentType: "post",
  }),
});
        } catch {}
      }
    } finally {
      setTimeout(() => fetchShareCount(), 220);
    }
  };

  const quickSaveToAllIfNeeded = () => {
    if (!postId) return;
    const me = getCurrentUserId?.();
    if (!me) {
      navigate?.("/auth", { state: { from: `/post/${postId}` } });
      return;
    }
    setSaveSheetOpen(true);
  };

  const handleLikeClick = async (e) => {
    e?.stopPropagation?.();
    if (!postId) return;

    const me = getCurrentUserId?.();
    if (!me) {
      navigate?.("/auth", { state: { from: `/post/${postId}` } });
      return;
    }

    const wasLiked = !!likedStateRef.current;
    const willBeLiked = !wasLiked;

    const curAbs = Number.isFinite(likeUiRef.current) ? likeUiRef.current : 0;
    const delta = willBeLiked ? 1 : -1;
    const expectedAbs = Math.max(0, curAbs + delta);

    // 1) миттєво UI
    setLocalLiked(willBeLiked);
    likedStateRef.current = willBeLiked;
    setLikeCount(expectedAbs);
    onSyncCounts?.({ postId, likesCount: expectedAbs });

    // 2) guard
    likeMutationRef.current = {
      ts: Date.now(),
      expected: expectedAbs,
      expectedLiked: willBeLiked,
    };

    // 3) глобальна подія
    try {
      window.dispatchEvent(
        new CustomEvent("cv-likes-updated", {
          detail: {
            postId,
            liked: willBeLiked,
            delta,
            absolute: expectedAbs,
          },
        })
      );
    } catch {}

    try {
  if (typeof onToggleLike === "function") {
    onToggleLike(item);
  } else {
    const token = await getAccessToken();
if (!token) {
  navigate?.("/auth", { state: { from: `/post/${postId}` } });
  return;
}

await fetch(apiUrl(`/api/interactions/like`), {
  method: "POST",
  headers: {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  },
  body: JSON.stringify({
    id: postId,
    type: "post",
  }),
});
  }
} catch (err) {
  console.error("[post like toggle failed]", err);
} 
  };

  const [aggLoading, setAggLoading] = useState(true);
  const [autoHidden, setAutoHidden] = useState(false);

  

  const computedTime = timeAgo || formatTimeAgo(item?.createdAt);
  const metaLine = item?.location ? `${item.location} • ${computedTime}` : computedTime;

  const [expanded, setExpanded] = useState(false);
  const textRef = useRef(null);
  const [needClamp, setNeedClamp] = useState(false);
  const [collapsedMax, setCollapsedMax] = useState(0);
  const [fullHeight, setFullHeight] = useState(0);

  useEffect(() => {
    const measure = () => {
      const el = textRef.current;
      if (!el) return;

      const cs = window.getComputedStyle(el);
      const lh =
        cs.lineHeight === "normal"
          ? parseFloat(cs.fontSize) * 1.5
          : parseFloat(cs.lineHeight || "20");

      const collapsed = Math.round(lh * 3 + 2);
      setCollapsedMax(collapsed);

      const prev = el.style.maxHeight;
      el.style.maxHeight = "none";
      const full = el.scrollHeight;
      el.style.maxHeight = prev;

      setFullHeight(full);
      setNeedClamp(full > collapsed + 2);
    };

    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [postId, item?.text]);

  const header = (
    <div className="post__header">
      <img
        src={item?.authorAvatar || DEFAULT_AVATAR}
        alt="avatar"
        className="post__avatar"
        onClick={(e) => {
          e.stopPropagation();
          navigate?.(`/profile/${item?.userId}`);
        }}
      />
      <div
        className="post__author-info"
        onClick={(e) => {
          e.stopPropagation();
          navigate?.(`/profile/${item?.userId}`);
        }}
      >
        <div className="post__author-name">{item?.authorName}</div>
        <div className="post__meta">{metaLine}</div>
      </div>

      <button
        className="post__more"
        onClick={(e) => {
          e.stopPropagation();
          onOpenActions?.();
        }}
        aria-label="Дії"
        title="Дії"
      >
        ⋮
      </button>
    </div>
  );

  const hasPhoto =
    !!item?.photo || (Array.isArray(item?.photos) && item.photos.length > 0);

  const title =
    item?.title || (item?.text ? item.text.slice(0, 60) : "Пост");

  const photosArr = useMemo(() => {
    if (Array.isArray(item?.photos) && item.photos.length) {
      return item.photos
        .map((p) => {
          if (typeof p === "string") return { src: p, alt: title };
          if (p && (p.url || p.src)) return { src: p.url || p.src, alt: p.alt || title };
          return null;
        })
        .filter(Boolean);
    }
    if (item?.photo) return [{ src: item.photo, alt: title }];
    return [];
  }, [item, title]);

  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerIdx, setViewerIdx] = useState(0);
  const doubleTapRef = useRef(false);
  const armDoubleTapGuard = () => {
    doubleTapRef.current = true;
    setTimeout(() => (doubleTapRef.current = false), 260);
  };

  const showUnderReview = !aggLoading && autoHidden && ownerView;

  if (hasPhoto) {
  const imgClass = showUnderReview
    ? "feed__image is-blurred"
    : "feed__image";

  return (
    <div className="post-card">
      {header}

      <div className="feed__media">
        <div className="feed__image-outer">
          <div
            className={`feed__image-clip ${
              showUnderReview ? "under-review" : ""
            }`}
            onDoubleClick={() => {
              if (!localLiked) {
                handleLikeClick();
              }

              armDoubleTapGuard();
            }}
          >
            <img
              src={photosArr[0]?.src || item.photo}
              alt={title}
              className={imgClass}
              draggable={false}
              onClick={(event) => {
                event.stopPropagation();

                if (doubleTapRef.current) return;

                setViewerIdx(0);
                setViewerOpen(true);
              }}
            />

            {showUnderReview && (
              <div className="under-review__overlay">
                <div className="under-review__badge">
                  Приховано через скарги
                </div>

                <div className="under-review__text">
                  Публікація на перевірці модератора
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="feed__caption feed__caption--compact">
          {item?.title && (
            <div className="feed__caption-title feed__caption-title--bold">
              {item.title}
            </div>
          )}

          {item?.text && (
            <div className="feed__desc">
              <div
                className={`collapsible-text ${
                  expanded
                    ? "is-expanded"
                    : "is-collapsed"
                }`}
                style={{
                  maxHeight: expanded
                    ? fullHeight
                      ? `${fullHeight}px`
                      : "9999px"
                    : collapsedMax
                    ? `${collapsedMax}px`
                    : "4.5em",
                }}
              >
                <div
                  ref={textRef}
                  className="feed__caption-text feed__caption-text--normal"
                >
                  {item.text}
                </div>
              </div>

              {needClamp && (
                <button
                  type="button"
                  className={`readmore-btn ${
                    expanded ? "is-open" : ""
                  }`}
                  onClick={() =>
                    setExpanded((value) => !value)
                  }
                  aria-expanded={expanded}
                >
                  {expanded
                    ? "Згорнути"
                    : "Розгорнути"}
                </button>
              )}
            </div>
          )}
        </div>

        <ActionBar
          variant="inline"
          clean
          showCounts={false}
          compact
          showCounts={false}
          liked={localLiked}
          saved={localSaved}
          likeCount={likeCount}
          commentCount={commentCount}
          shareCount={shareCount}
          savedCount={savedCount}
          onLike={handleLikeClick}
          onComment={handleOpenComments}
          onShare={openShare}
          onSave={(event) => {
            event?.stopPropagation?.();
            quickSaveToAllIfNeeded();
          }}
          showComments={allowComments}
        />
      </div>

      {!onOpenComments &&
        allowComments &&
        createPortal(
          <CommentsModal
            open={commentsOpen}
            listingId={postId}
            listingAuthorId={authorId}
            onClose={() => {
              setCommentsOpen(false);
              fetchCommentCount();
            }}
            onCountChange={(deltaOrAbs) => {
              const value = Number(deltaOrAbs);

              if (!Number.isFinite(value)) {
                return;
              }

              const looksLikeDelta =
                value === -1 ||
                value === 0 ||
                value === 1;

              setCommentCount((previous) => {
                const current = Number.isFinite(previous)
                  ? previous
                  : 0;

                const next = looksLikeDelta
                  ? Math.max(0, current + value)
                  : Math.max(0, value);

                try {
                  window.dispatchEvent(
                    new CustomEvent(
                      "cv-comments-updated",
                      {
                        detail: {
                          postId,
                          delta: looksLikeDelta
                            ? value
                            : next - current,
                          absolute: next,
                        },
                      }
                    )
                  );
                } catch {}

                setTimeout(
                  () => fetchCommentCount(),
                  140
                );

                return next;
              });
            }}
          />,
          document.getElementById("modal-root") ||
            document.body
        )}

      {createPortal(
        <ShareModal
          open={shareOpen}
          onClose={() => {
            setShareOpen(false);
            fetchShareCount();
          }}
          shareUrl={shareUrl}
          shareText={shareText}
          onShared={handleShared}
        />,
        document.getElementById("modal-root") ||
          document.body
      )}

      {createPortal(
        <SaveSheetModal
          open={saveSheetOpen}
          onClose={() =>
            setSaveSheetOpen(false)
          }
          userId={getCurrentUserId?.()}
          postId={postId}
          type="post"
          thumbnail={item?.photo}
          title="Збереження"
          onChanged={async ({ saved }) => {
            const wasSaved =
              !!savedStateRef.current;

            const willBeSaved = !!saved;

            setLocalSaved(willBeSaved);
            savedStateRef.current =
              willBeSaved;

            const delta =
              !wasSaved && willBeSaved
                ? 1
                : wasSaved && !willBeSaved
                ? -1
                : 0;

            const currentAbsolute =
              Number.isFinite(
                savedUiRef.current
              )
                ? savedUiRef.current
                : 0;

            const expectedAbsolute =
              Math.max(
                0,
                currentAbsolute + delta
              );

            if (delta !== 0) {
              setSavedCount(
                expectedAbsolute
              );
            }

            savedMutationRef.current = {
              ts: Date.now(),
              expected:
                expectedAbsolute,
              expectedSaved:
                willBeSaved,
            };

            try {
              window.dispatchEvent(
                new CustomEvent(
                  "cv-saved-updated",
                  {
                    detail: {
                      postId,
                      saved: willBeSaved,
                      delta,
                      absolute:
                        expectedAbsolute,
                    },
                  }
                )
              );
            } catch {}

            setTimeout(() => {
              fetchSavedCount();
              fetchSavedState();
            }, 220);
          }}
        />,
        document.getElementById("modal-root") ||
          document.body
      )}

      <PhotoDetails
        isOpen={viewerOpen}
        photos={photosArr}
        startIndex={viewerIdx}
        onClose={() =>
          setViewerOpen(false)
        }
      />
    </div>
  );
}

  const textOnlyClass = `cf-card text-only ${showUnderReview ? "under-review" : ""}`;

  return (
    <div className="post-card">
      {header}

      <div className="feed__media">
        <div className={textOnlyClass}>
          {showUnderReview && (
            <div className="under-review__overlay">
              <div className="under-review__badge">Приховано через скарги</div>
              <div className="under-review__text">Публікація на перевірці модератора</div>
            </div>
          )}

          {item?.text && (
            <div className="feed__desc feed__desc--textonly">
              <div
                className={`collapsible-text ${expanded ? "is-expanded" : "is-collapsed"}`}
                style={{
                  maxHeight: expanded
                    ? (fullHeight ? `${fullHeight}px` : "9999px")
                    : (collapsedMax ? `${collapsedMax}px` : "3.2em"),
                }}
              >
                <div ref={textRef} className="cf-info">
                  {item.text}
                </div>
              </div>

              {needClamp && (
                <button
                  type="button"
                  className={`readmore-btn ${expanded ? "is-open" : ""}`}
                  onClick={() => setExpanded((v) => !v)}
                  aria-expanded={expanded}
                >
                  {expanded ? "Згорнути" : "Читати далі"}
                </button>
              )}
            </div>
          )}

          <ActionBar
            variant="inline"
            clean
            showCounts={false}
            compact
            showCounts={false}
            liked={localLiked}
            saved={localSaved}
            likeCount={likeCount}
            commentCount={commentCount}
            shareCount={shareCount}
            savedCount={savedCount}
            onLike={handleLikeClick}
            onComment={handleOpenComments}
            onShare={openShare}
            onSave={(e) => {
              e?.stopPropagation?.();
              quickSaveToAllIfNeeded();
            }}
            showComments={allowComments}
          />

        </div>
      </div>

      {!onOpenComments && allowComments &&
        createPortal(
          <CommentsModal
            open={commentsOpen}
            listingId={postId}
            listingAuthorId={authorId}
            onClose={() => {
              setCommentsOpen(false);
              fetchCommentCount();
            }}
            onCountChange={(deltaOrAbs) => {
              const v = Number(deltaOrAbs);
              if (!Number.isFinite(v)) return;

              const looksLikeDelta = v === -1 || v === 0 || v === 1;

              setCommentCount((prev) => {
                const cur = Number.isFinite(prev) ? prev : 0;
                const next = looksLikeDelta ? Math.max(0, cur + v) : Math.max(0, v);

                try {
                  window.dispatchEvent(
                    new CustomEvent("cv-comments-updated", {
                      detail: {
                        postId,
                        delta: looksLikeDelta ? v : (next - cur),
                        absolute: next,
                      },
                    })
                  );
                } catch {}

                setTimeout(() => fetchCommentCount(), 140);

                return next;
              });
            }}
          />,
          document.getElementById("modal-root") || document.body
        )
      }

      {createPortal(
        <ShareModal
          open={shareOpen}
          onClose={() => {
            setShareOpen(false);
            fetchShareCount();
          }}
          shareUrl={shareUrl}
          shareText={shareText}
          onShared={handleShared}
        />,
        document.getElementById("modal-root") || document.body
      )}

      {createPortal(
        <SaveSheetModal
          open={saveSheetOpen}
          onClose={() => setSaveSheetOpen(false)}
          userId={getCurrentUserId?.()}
          postId={postId}
          type="post"
          thumbnail={null}
          title="Збереження"
          onChanged={async ({ saved }) => {
            const wasSaved = !!savedStateRef.current;
            const willBeSaved = !!saved;

            setLocalSaved(willBeSaved);
            savedStateRef.current = willBeSaved;

            const delta =
              !wasSaved && willBeSaved ? 1 :
              wasSaved && !willBeSaved ? -1 :
              0;

            const curAbs = Number.isFinite(savedUiRef.current) ? savedUiRef.current : 0;
            const expectedAbs = Math.max(0, curAbs + delta);

            if (delta !== 0) setSavedCount(expectedAbs);

            savedMutationRef.current = {
              ts: Date.now(),
              expected: expectedAbs,
              expectedSaved: willBeSaved,
            };

            try {
              window.dispatchEvent(
                new CustomEvent("cv-saved-updated", {
                  detail: {
                    postId,
                    saved: willBeSaved,
                    delta,
                    absolute: expectedAbs,
                  },
                })
              );
            } catch {}

            setTimeout(() => {
              fetchSavedCount();
              fetchSavedState();
            }, 220);
          }}
        />,
        document.getElementById("modal-root") || document.body
      )}
    </div>
  );
}